import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, Link2, Pencil, Plus, Trash2 } from 'lucide-react';

import { CustomToolModal } from '@/components/configurations/CustomToolModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfigurations, useUpdateConfiguration } from '@/hooks/useConfigurations';
import { useScrapers } from '@/hooks/useScrapers';
import {
  useCreateCustomTool,
  useCustomTools,
  useCustomToolUsage,
  useDeleteCustomTool,
  useUpdateCustomTool,
} from '@/hooks/useTools';
import { toolsApi } from '@/lib/api/tools.api';

import type {
  CreateCustomToolDto,
  CustomToolDetail,
  UpdateCustomToolDto,
} from '@repo/shared-types';

const NONE = '__none__';

/** Renders the configurations a tool is attached to, via the per-tool usage endpoint. */
const ToolConfigsCell = ({ toolId }: { toolId: string }) => {
  const { data, isLoading } = useCustomToolUsage(toolId);
  const configs = data?.configurations ?? [];
  if (isLoading) return <span className="text-muted-foreground text-xs">…</span>;
  if (configs.length === 0)
    return <span className="text-muted-foreground text-xs">Not assigned</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {configs.slice(0, 3).map((c) => (
        <Badge key={c.id} variant="outline">
          {c.name}
        </Badge>
      ))}
      {configs.length > 3 && <Badge variant="outline">+{configs.length - 3}</Badge>}
    </div>
  );
};

export const CustomToolsPage = () => {
  const queryClient = useQueryClient();
  const { data: toolsData, isLoading } = useCustomTools({
    page: 1,
    limit: 100,
    sortBy: 'name',
    sortOrder: 'asc',
  });
  const { data: scrapers = [] } = useScrapers();
  const createMut = useCreateCustomTool();
  const updateMut = useUpdateCustomTool();
  const deleteMut = useDeleteCustomTool();
  const updateConfigMut = useUpdateConfiguration();

  const tools = toolsData?.data ?? [];
  const scraperName = (id?: string | null) =>
    id ? (scrapers.find((s) => s.id === id)?.name ?? 'unknown') : null;

  // ── Create / edit tool (reuses the configuration CustomToolModal) ──
  const [editModal, setEditModal] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    tool: CustomToolDetail | null;
  }>({ open: false, mode: 'create', tool: null });
  const [modalErrors, setModalErrors] = useState<Array<{ field: string; message: string }>>([]);

  const handleModalSubmit = async (values: CreateCustomToolDto | UpdateCustomToolDto) => {
    setModalErrors([]);
    try {
      if (editModal.mode === 'edit' && editModal.tool) {
        await updateMut.mutateAsync({ id: editModal.tool.id, data: values });
      } else {
        await createMut.mutateAsync(values as CreateCustomToolDto);
      }
      setEditModal({ open: false, mode: 'create', tool: null });
    } catch (e) {
      setModalErrors([
        { field: 'root', message: e instanceof Error ? e.message : 'Failed to save' },
      ]);
    }
  };

  // ── Manage assignment (scraper + configurations) ──
  const [manage, setManage] = useState<{
    open: boolean;
    tool: CustomToolDetail | null;
    scraperId: string;
    configIds: Set<string>;
  }>({ open: false, tool: null, scraperId: NONE, configIds: new Set() });
  const [manageError, setManageError] = useState('');
  // Full configuration list is only needed while the Manage dialog is open — load it lazily.
  const { data: configs = [] } = useConfigurations(manage.open);

  const openManage = async (tool: CustomToolDetail) => {
    setManage({ open: true, tool, scraperId: tool.scraper_id ?? NONE, configIds: new Set() });
    setManageError('');
    // Pre-check currently-assigned configs from the per-tool usage endpoint.
    try {
      const usage = await toolsApi.getCustomToolUsage(tool.id);
      const assigned = new Set(usage.configurations.map((c) => c.id));
      setManage((m) => (m.tool?.id === tool.id ? { ...m, configIds: assigned } : m));
    } catch {
      /* leave unchecked on error */
    }
  };

  const toggleConfig = (id: string) =>
    setManage((m) => {
      const next = new Set(m.configIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...m, configIds: next };
    });

  const saveManage = async () => {
    if (!manage.tool) return;
    setManageError('');
    const tool = manage.tool;
    try {
      // 1) scraper assignment
      const newScraperId = manage.scraperId === NONE ? null : manage.scraperId;
      if ((tool.scraper_id ?? null) !== newScraperId) {
        await updateMut.mutateAsync({ id: tool.id, data: { scraper_id: newScraperId } });
      }
      // 2) configuration membership diff
      for (const cfg of configs) {
        const was = (cfg.custom_tool_ids ?? []).includes(tool.id);
        const now = manage.configIds.has(cfg.id);
        if (was === now) continue;
        const nextIds = now
          ? [...(cfg.custom_tool_ids ?? []), tool.id]
          : (cfg.custom_tool_ids ?? []).filter((t) => t !== tool.id);
        await updateConfigMut.mutateAsync({
          id: cfg.id,
          data: { custom_tool_ids: nextIds } as never,
        });
      }
      // Refresh the per-tool usage cells (config membership changed).
      await queryClient.invalidateQueries({ queryKey: ['custom-tools'] });
      setManage({ open: false, tool: null, scraperId: NONE, configIds: new Set() });
    } catch (e) {
      setManageError(e instanceof Error ? e.message : 'Failed to save assignment');
    }
  };

  const handleDelete = (tool: CustomToolDetail) => {
    if (confirm(`Delete "${tool.name}"? It will also be removed from any configurations using it.`))
      deleteMut.mutate({ id: tool.id, force: true });
  };

  const editInitialValues = (tool: CustomToolDetail): CreateCustomToolDto =>
    ({
      name: tool.name,
      description: tool.description,
      http_method: tool.http_method,
      url: tool.url,
      headers: tool.headers ?? {},
      parameters_schema: tool.parameters_schema,
      response_mapping: tool.response_mapping,
      is_active: tool.is_active ?? true,
      is_external: tool.is_external ?? false,
      idempotency: tool.idempotency ?? 'none',
      display_config: tool.display_config ?? null,
      scraper_id: tool.scraper_id ?? null,
    }) as CreateCustomToolDto;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Custom Tools</h1>
          <p className="text-muted-foreground text-sm">
            Voice/config custom tools. See which configurations use each tool and which scraper (if
            any) backs it; assign both from here.
          </p>
        </div>
        <Button onClick={() => setEditModal({ open: true, mode: 'create', tool: null })}>
          <Plus className="mr-2 h-4 w-4" /> New Tool
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Scraper</TableHead>
                <TableHead>Configurations</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Watch</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7}>Loading…</TableCell>
                </TableRow>
              )}
              {!isLoading && tools.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-muted-foreground">
                    No custom tools yet.
                  </TableCell>
                </TableRow>
              )}
              {tools.map((tool) => (
                <TableRow key={tool.id}>
                  <TableCell>
                    <div className="font-medium">{tool.name}</div>
                    <div className="text-muted-foreground max-w-md truncate text-xs">
                      {tool.description}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{tool.http_method}</Badge>
                  </TableCell>
                  <TableCell>
                    {tool.scraper_id ? (
                      <Badge variant="secondary">{scraperName(tool.scraper_id)}</Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ToolConfigsCell toolId={tool.id} />
                  </TableCell>
                  <TableCell>{tool.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      title={
                        tool.watch_enabled
                          ? 'Watching — invocations notify and highlight the contact. Click to stop.'
                          : 'Not watching — click to get notified when this tool is invoked.'
                      }
                      disabled={updateMut.isPending}
                      onClick={() =>
                        updateMut.mutate({
                          id: tool.id,
                          data: { watch_enabled: !tool.watch_enabled },
                        })
                      }
                    >
                      {tool.watch_enabled ? (
                        <Bell className="h-4 w-4 text-amber-500" />
                      ) : (
                        <BellOff className="text-muted-foreground h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Assign scraper / configs"
                      onClick={() => openManage(tool)}
                    >
                      <Link2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Edit tool"
                      onClick={() => setEditModal({ open: true, mode: 'edit', tool })}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Delete"
                      onClick={() => handleDelete(tool)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / edit tool definition */}
      <CustomToolModal
        open={editModal.open}
        onOpenChange={(open) => setEditModal((s) => ({ ...s, open }))}
        mode={editModal.mode}
        initialValues={
          editModal.mode === 'edit' && editModal.tool
            ? editInitialValues(editModal.tool)
            : undefined
        }
        onSubmit={handleModalSubmit}
        errors={modalErrors}
        isLoading={createMut.isPending || updateMut.isPending}
      />

      {/* Manage assignment */}
      <Dialog open={manage.open} onOpenChange={(open) => setManage((m) => ({ ...m, open }))}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assign — {manage.tool?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium">Scraper</label>
              <p className="text-muted-foreground mb-1 text-xs">
                When set, this tool fetches + parses the site via the scraper instead of calling its
                URL.
              </p>
              <Select
                value={manage.scraperId}
                onValueChange={(v) => setManage((m) => ({ ...m, scraperId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No scraper" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No scraper (plain HTTP)</SelectItem>
                  {scrapers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium">Configurations</label>
              <p className="text-muted-foreground mb-2 text-xs">
                Attach this tool to the configurations that should expose it.
              </p>
              <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-2">
                {configs.length === 0 && (
                  <p className="text-muted-foreground p-2 text-xs">Loading configurations…</p>
                )}
                {configs.map((cfg) => (
                  <label
                    key={cfg.id}
                    className="hover:bg-accent flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={manage.configIds.has(cfg.id)}
                      onChange={() => toggleConfig(cfg.id)}
                    />
                    {cfg.name}
                  </label>
                ))}
              </div>
            </div>

            {manageError && <p className="text-destructive text-sm">{manageError}</p>}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManage((m) => ({ ...m, open: false }))}>
              Cancel
            </Button>
            <Button
              onClick={saveManage}
              disabled={updateMut.isPending || updateConfigMut.isPending}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
